import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Shield, ShieldCheck, ShieldPlus, FileText, Plus, Calendar, Phone,
  CheckCircle, AlertTriangle, AlertCircle, ChevronRight, Clock, Building
} from 'lucide-react';
import API from '@/utils/api';

// Protection type configurations
const PROTECTION_CONFIG = {
  registration: { 
    label: 'Registration', 
    icon: FileText,
    color: 'bg-blue-100 text-blue-700',
    description: 'Vehicle registration renewal'
  },
  insurance: { 
    label: 'Insurance', 
    icon: Shield,
    color: 'bg-purple-100 text-purple-700',
    description: 'Auto insurance coverage'
  },
  warranty: { 
    label: 'Warranty', 
    icon: ShieldCheck,
    color: 'bg-green-100 text-green-700',
    description: 'Manufacturer warranty'
  },
  extended_warranty: { 
    label: 'Extended Warranty', 
    icon: ShieldPlus,
    color: 'bg-teal-100 text-teal-700',
    description: 'Extended protection plan'
  },
  service_contract: { 
    label: 'Service Contract', 
    icon: FileText,
    color: 'bg-amber-100 text-amber-700',
    description: 'Prepaid maintenance plan'
  },
};

// Status configurations - handles both API responses (active/expiring_soon/expired) and health data (ok/due_soon/overdue)
const STATUS_CONFIG = {
  // API status values
  active: { label: 'Active', bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  expiring_soon: { label: 'Expiring Soon', bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
  expired: { label: 'Expired', bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle },
  // Health data status values (for backward compatibility)
  overdue: { label: 'Expired', bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle },
  due_soon: { label: 'Expiring Soon', bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
  ok: { label: 'Active', bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  not_set: { label: 'Ready to Add', bg: 'bg-slate-100', text: 'text-slate-500', icon: Clock },
};

const ProtectionTab = ({ 
  vehicleId,
  healthData,
  onAddProtection,
  onRefresh,
  theme 
}) => {
  const [protections, setProtections] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProtections = useCallback(async () => {
    try {
      const response = await API.get(`/protections?vehicle_id=${vehicleId}`);
      setProtections(response.data);
    } catch (error) {
      console.error('Failed to fetch protections:', error);
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    fetchProtections();
  }, [fetchProtections]);

  // Group protections by type
  const protectionsByType = protections.reduce((acc, p) => {
    const type = p.protection_type || 'warranty';
    if (!acc[type]) acc[type] = [];
    acc[type].push(p);
    return acc;
  }, {});

  // Also include items from healthData.protection_items (from old warranty system)
  const protectionItems = healthData?.protection_items || [];

  // Quick add buttons for common types
  const quickAddTypes = ['registration', 'insurance', 'warranty'];

  return (
    <div className="space-y-4">
      {/* Quick Add Section */}
      <Card style={{ backgroundColor: theme.surface }}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Shield className="h-5 w-5" style={{ color: theme.icon }} />
            Add Protection
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-3 gap-2">
            {quickAddTypes.map((type) => {
              const config = PROTECTION_CONFIG[type];
              const Icon = config.icon;
              return (
                <Button
                  key={type}
                  variant="outline"
                  className="flex flex-col h-auto py-3 gap-1"
                  onClick={() => onAddProtection(type)}
                >
                  <Icon className="h-5 w-5" style={{ color: theme.icon }} />
                  <span className="text-xs">{config.label}</span>
                </Button>
              );
            })}
          </div>
          <Button
            variant="ghost"
            className="w-full mt-2 text-sm"
            onClick={() => onAddProtection()}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Other Protection
          </Button>
        </CardContent>
      </Card>

      {/* Protection Status Summary */}
      {healthData?.protection_stats && (
        <div className="grid grid-cols-3 gap-2">
          <Card style={{ backgroundColor: theme.surface }}>
            <CardContent className="p-3 text-center">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-1">
                <CheckCircle className="h-4 w-4 text-green-600" />
              </div>
              <p className="text-lg font-bold text-green-600">{healthData.protection_stats.ok || 0}</p>
              <p className="text-xs text-slate-500">Active</p>
            </CardContent>
          </Card>
          <Card style={{ backgroundColor: theme.surface }}>
            <CardContent className="p-3 text-center">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-1">
                <Clock className="h-4 w-4 text-amber-600" />
              </div>
              <p className="text-lg font-bold text-amber-600">{healthData.protection_stats.due_soon || 0}</p>
              <p className="text-xs text-slate-500">Expiring</p>
            </CardContent>
          </Card>
          <Card style={{ backgroundColor: theme.surface }}>
            <CardContent className="p-3 text-center">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-1">
                <AlertCircle className="h-4 w-4 text-red-600" />
              </div>
              <p className="text-lg font-bold text-red-600">{healthData.protection_stats.overdue || 0}</p>
              <p className="text-xs text-slate-500">Expired</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Protection Items List */}
      {loading ? (
        <div className="text-center py-8 text-slate-400">Loading...</div>
      ) : (protections.length === 0 && protectionItems.length === 0) ? (
        <Card style={{ backgroundColor: theme.surface }}>
          <CardContent className="py-8 text-center text-slate-400">
            <Shield className="h-12 w-12 mx-auto mb-2 opacity-30" />
            <p className="font-medium text-slate-500">Add Your Protection Info</p>
            <p className="text-sm">Track your registration, insurance, and warranties in one place</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* New Protection System Items */}
          {protections.map((protection) => {
            const typeConfig = PROTECTION_CONFIG[protection.protection_type] || PROTECTION_CONFIG.warranty;
            const statusConfig = STATUS_CONFIG[protection.status] || STATUS_CONFIG.active;
            const TypeIcon = typeConfig.icon;
            const StatusIcon = statusConfig.icon;

            return (
              <Card 
                key={protection.id}
                className={`border ${statusConfig.bg.replace('bg-', 'border-').replace('-100', '-200')}`}
                style={{ backgroundColor: theme.surface }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${statusConfig.bg}`}>
                      <TypeIcon className={`h-6 w-6 ${statusConfig.text}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-slate-800">{typeConfig.label}</h4>
                        <Badge className={`${statusConfig.bg} ${statusConfig.text} text-xs`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </div>
                      
                      {/* Provider */}
                      {protection.provider && (
                        <div className="flex items-center gap-1 text-sm text-slate-600 mb-1">
                          <Building className="h-3 w-3" />
                          {protection.provider}
                        </div>
                      )}

                      {/* Details */}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
                        {protection.policy_number && (
                          <span>Policy: {protection.policy_number}</span>
                        )}
                        {protection.expiration_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Expires: {protection.expiration_date}
                          </span>
                        )}
                        {protection.expiration_mileage && (
                          <span>or {protection.expiration_mileage.toLocaleString()} mi</span>
                        )}
                      </div>

                      {/* Contact */}
                      {protection.claim_phone && (
                        <div className="flex items-center gap-1 mt-2 text-sm text-slate-500">
                          <Phone className="h-3 w-3" />
                          {protection.claim_phone}
                        </div>
                      )}
                    </div>

                    <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Legacy Protection Items from healthData */}
          {protectionItems.filter(p => !protections.some(np => np.id === p.id)).map((item) => {
            const typeConfig = PROTECTION_CONFIG[item.type] || PROTECTION_CONFIG.warranty;
            const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.active;
            const TypeIcon = typeConfig.icon;
            const StatusIcon = statusConfig.icon;

            return (
              <Card 
                key={item.id || item.type}
                className={`border ${statusConfig.bg.replace('bg-', 'border-').replace('-100', '-200')}`}
                style={{ backgroundColor: theme.surface }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${statusConfig.bg}`}>
                      <TypeIcon className={`h-6 w-6 ${statusConfig.text}`} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-slate-800">{item.name || typeConfig.label}</h4>
                        <Badge className={`${statusConfig.bg} ${statusConfig.text} text-xs`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </div>
                      
                      {item.provider && (
                        <div className="flex items-center gap-1 text-sm text-slate-600 mb-1">
                          <Building className="h-3 w-3" />
                          {item.provider}
                        </div>
                      )}

                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
                        {item.expiration_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Expires: {item.expiration_date}
                          </span>
                        )}
                        {item.time_remaining?.text && (
                          <span className={item.time_remaining.overdue ? 'text-red-600' : ''}>
                            ({item.time_remaining.text})
                          </span>
                        )}
                      </div>

                      {item.claim_phone && (
                        <div className="flex items-center gap-1 mt-2 text-sm text-slate-500">
                          <Phone className="h-3 w-3" />
                          {item.claim_phone}
                        </div>
                      )}
                    </div>

                    <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProtectionTab;
